/** Presets toolbar button + dropdown for data grids.
 *
 * Popover (not Menu — a search TextField inside Menu fights its typeahead
 * focus) with a client-side-filtered list of the grid's presets, a per-item
 * overflow menu (rename/delete, OWN presets only), the "Save current view
 * as preset…" entry, and — for console_presets_manage holders — a gear icon
 * linking to the /presets management page. Clicking a preset loads it
 * (URL → `?preset=<id>`).
 */
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as presetsApi from '@/api/presets';
import { presetErrorMessage, type GridPresetListItem } from '@/api/presets';
import { useOptionalAuth } from '@/auth/AuthProvider';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import type { PresetsToolbarConfig } from '@/hooks/useGridPresets';
import { formatRelativeTime } from '@/utils/format';

import { SavePresetDialog } from './SavePresetDialog';

function ownerName(p: GridPresetListItem): string {
  const full = `${p.owner.first_name} ${p.owner.last_name}`.trim();
  return full || p.owner.email;
}

export function PresetsMenu({ presets }: { presets: PresetsToolbarConfig }) {
  const auth = useOptionalAuth();
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const [saveOpen, setSaveOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState<{ el: HTMLElement; preset: GridPresetListItem } | null>(
    null,
  );
  const [renaming, setRenaming] = useState<GridPresetListItem | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<GridPresetListItem | null>(null);

  const open = Boolean(anchor);
  const listQuery = useQuery({
    queryKey: ['gridPresets', presets.grid],
    queryFn: () => presetsApi.list({ grid: presets.grid, limit: 200, ordering: '-updated_ts' }),
    enabled: open,
    staleTime: 30_000,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['gridPresets', presets.grid] });
    void qc.invalidateQueries({ queryKey: ['gridPresetsAll'] });
  };

  const renameMutation = useMutation({
    mutationFn: (input: { id: number; name: string }) =>
      presetsApi.update(input.id, { name: input.name }),
    onSuccess: () => {
      invalidate();
      snackbar.show('Preset renamed.', 'success');
      setRenaming(null);
    },
    onError: (e: unknown) => setRenameError(presetErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => presetsApi.remove(id),
    onSuccess: (_, id) => {
      invalidate();
      qc.removeQueries({ queryKey: ['gridPreset', id] });
      snackbar.show('Preset deleted.', 'success');
      if (presets.mode.kind === 'preset' && presets.mode.presetId === id) {
        presets.clearPreset();
      }
      setDeleting(null);
    },
    onError: (e: unknown) => {
      snackbar.show(presetErrorMessage(e), 'error');
      setDeleting(null);
    },
  });

  const close = () => {
    setAnchor(null);
    setSearch('');
  };

  const activeId = presets.mode.kind === 'preset' ? presets.mode.presetId : null;
  const rows = (listQuery.data?.results ?? []).filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return p.name.toLowerCase().includes(q) || ownerName(p).toLowerCase().includes(q);
  });

  return (
    <>
      <Tooltip title="Presets">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          color={activeId !== null ? 'primary' : 'default'}
        >
          <BookmarksIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 360, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder="Search presets…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {auth?.hasPerm?.('console_presets_manage') && (
              <Tooltip title="Manage presets">
                <IconButton
                  size="small"
                  component={RouterLink}
                  to="/presets"
                  aria-label="Manage presets"
                  onClick={close}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <List dense disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
            {listQuery.isLoading && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                Loading…
              </Typography>
            )}
            {!listQuery.isLoading && rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                {search ? 'No presets match.' : 'No presets for this grid yet.'}
              </Typography>
            )}
            {rows.map((p) => {
              const own = auth?.user != null && p.owner.id === auth.user.id;
              const isDefault = presets.defaultPresetId === p.id;
              return (
                <ListItemButton
                  key={p.id}
                  dense
                  selected={p.id === activeId}
                  onClick={() => {
                    presets.loadPreset(p.id);
                    close();
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{p.id}
                        </Typography>
                        {p.visibility === 'shared' && (
                          <Tooltip title="Shared with all console users">
                            <PeopleAltIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={`${ownerName(p)} · ${formatRelativeTime(p.updated_ts)}`}
                    secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                  />
                  <Tooltip title={isDefault ? 'Default view — click to clear' : 'Set as default view'}>
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={isDefault ? `Clear default (${p.name})` : `Set ${p.name} as default`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDefault) {
                          presets.clearDefault();
                          snackbar.show('Default view cleared.', 'success');
                        } else {
                          presets.setAsDefault(p.id);
                          snackbar.show(`“${p.name}” is now the default view.`, 'success');
                        }
                      }}
                    >
                      {isDefault
                        ? <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                        : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  {own && (
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label={`Preset actions for ${p.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRowMenu({ el: e.currentTarget, preset: p });
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemButton>
              );
            })}
          </List>
          <Divider />
          <Box sx={{ p: 0.5 }}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                setSaveOpen(true);
                close();
              }}
            >
              Save current view as preset…
            </Button>
          </Box>
        </Box>
      </Popover>

      <Menu anchorEl={rowMenu?.el} open={Boolean(rowMenu)} onClose={() => setRowMenu(null)}>
        <MenuItem
          onClick={() => {
            if (rowMenu) {
              setRenaming(rowMenu.preset);
              setRenameValue(rowMenu.preset.name);
              setRenameError(null);
            }
            setRowMenu(null);
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenu) setDeleting(rowMenu.preset);
            setRowMenu(null);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(renaming)} onClose={() => setRenaming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename preset</DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            error={Boolean(renameError)}
            helperText={renameError}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!renameValue.trim() || renameMutation.isPending}
            onClick={() => {
              if (renaming) renameMutation.mutate({ id: renaming.id, name: renameValue.trim() });
            }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete preset"
        message={`Delete preset '${deleting?.name ?? ''}'? The change history is retained.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleting) deleteMutation.mutate(deleting.id);
        }}
        onCancel={() => setDeleting(null)}
      />

      <SavePresetDialog open={saveOpen} onClose={() => setSaveOpen(false)} presets={presets} />
    </>
  );
}
